import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, Rocket, Sparkles, Flame, Shield, Zap, Target, Users,
  XCircle, Trophy, TrendingUp, Eye, Heart, BarChart3, Award
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import ProductCard from "@/components/ProductCard";
import type { Tables } from "@/integrations/supabase/types";

type Product = Tables<"products">;

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

const Section = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  const { ref, visible } = useScrollReveal();
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}>
      {children}
    </div>
  );
};

const Landing = () => {
  const { t } = useTranslation();
  const [featured, setFeatured] = useState<(Product & { upvotes: number; interests: number })[]>([]);
  const [stats, setStats] = useState<{ projects: number; testers: number; interests: number } | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const [
        { count: projectCount },
        { data: interactionsData },
        { data: profilesData },
      ] = await Promise.all([
        supabase.from("products").select("*", { count: "exact", head: true }).eq("status", "published" as any),
        supabase.from("interactions").select("type"),
        supabase.from("public_profiles").select("user_type"),
      ]);

      const interests = interactionsData?.filter(i => i.type === "interest_user" || i.type === "interest_investor").length || 0;
      const testers = profilesData?.filter(p => (p.user_type as string[])?.includes("tester")).length || 0;
      const projects = projectCount || 0;

      if (projects > 0 || testers > 0 || interests > 0) {
        setStats({ projects, testers, interests });
      }

      const { data: products } = await supabase
        .from("products").select("*").eq("status", "published" as any).limit(20);
      if (!products || products.length === 0) return;
      const { data: allInteractions } = await supabase.from("interactions").select("product_id, type");
      const counts: Record<string, { upvotes: number; interests: number }> = {};
      allInteractions?.forEach((i) => {
        if (!counts[i.product_id]) counts[i.product_id] = { upvotes: 0, interests: 0 };
        if (i.type === "upvote") counts[i.product_id].upvotes++;
        else counts[i.product_id].interests++;
      });
      const enriched = products.map((p) => ({
        ...p,
        upvotes: counts[p.id]?.upvotes || 0,
        interests: counts[p.id]?.interests || 0,
      }));
      const now = new Date().toISOString();
      setFeatured(enriched.filter((p) => (p as any).is_featured_until && (p as any).is_featured_until > now));
    };
    fetchData();
  }, []);

  const pillars = [
    { icon: <Shield className="h-7 w-7" />, titleKey: "pillars.validation.title", descKey: "pillars.validation.desc", color: "text-primary" },
    { icon: <Zap className="h-7 w-7" />, titleKey: "pillars.growth.title", descKey: "pillars.growth.desc", color: "text-secondary" },
    { icon: <Target className="h-7 w-7" />, titleKey: "pillars.interest.title", descKey: "pillars.interest.desc", color: "text-primary" },
    { icon: <Users className="h-7 w-7" />, titleKey: "pillars.trust.title", descKey: "pillars.trust.desc", color: "text-secondary" },
  ];

  const gamificationItems = [
    { icon: <Award className="h-8 w-8" />, titleKey: "gamification.items.0", descKey: "" },
    { icon: <Eye className="h-8 w-8" />, titleKey: "gamification.items.1", descKey: "" },
    { icon: <TrendingUp className="h-8 w-8" />, titleKey: "gamification.items.2", descKey: "" },
  ];

  return (
    <div className="min-h-screen bg-background dark">
      <Navbar />

      <main>
      {/* ═══════ HERO ═══════ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full gradient-hero opacity-[0.07] blur-3xl" />
        </div>
        <div className="container mx-auto px-4 pt-20 pb-24 md:pt-32 md:pb-36 relative">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-accent px-4 py-1.5 text-sm text-accent-foreground font-medium">
              <Sparkles className="h-4 w-4" /> {t("hero.tagline")}
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight leading-[1.08] text-foreground">
              {t("hero.headline").split(".")[0]}.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {t("hero.subheadline")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-2">
              <Button size="lg" className="gradient-cta text-primary-foreground border-0 h-13 px-8 text-base font-semibold shadow-lg hover:opacity-90 transition-opacity" asChild>
                <Link to="/signup">{t("hero.cta")} <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button size="lg" variant="outline" className="h-13 px-8 text-base bg-background text-foreground hover:bg-accent hover:text-accent-foreground" asChild>
                <Link to="/discover">{t("nav.discover")}</Link>
              </Button>
            </div>

            {/* Mock dashboard — contextual metrics (never raw 0) */}
            <div className="mt-12 glass rounded-2xl p-6 max-w-3xl mx-auto">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="rounded-xl bg-muted/50 p-4 text-center space-y-1 min-h-[96px] flex flex-col justify-center">
                  <div className="flex justify-center"><Users className="h-5 w-5 text-primary" /></div>
                  {stats && stats.testers >= 5 ? (
                    <>
                      <p className="text-2xl font-bold text-foreground">{stats.testers}</p>
                      <p className="text-xs text-muted-foreground">{t("stats.testers")}</p>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground leading-snug pt-1">{t("stats.earlyTesters")}</p>
                  )}
                </div>
                <div className="rounded-xl bg-muted/50 p-4 text-center space-y-1 min-h-[96px] flex flex-col justify-center">
                  <div className="flex justify-center"><Heart className="h-5 w-5 text-destructive" /></div>
                  {stats && stats.interests >= 5 ? (
                    <>
                      <p className="text-2xl font-bold text-foreground">{stats.interests}</p>
                      <p className="text-xs text-muted-foreground">{t("stats.interests")}</p>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground leading-snug pt-1">{t("stats.earlyInterests")}</p>
                  )}
                </div>
                <div className="rounded-xl bg-muted/50 p-4 text-center space-y-1 min-h-[96px] flex flex-col justify-center">
                  <div className="flex justify-center"><Rocket className="h-5 w-5 text-primary" /></div>
                  {stats && stats.projects >= 5 ? (
                    <>
                      <p className="text-2xl font-bold text-foreground">{stats.projects}</p>
                      <p className="text-xs text-muted-foreground">{t("stats.projects")}</p>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground leading-snug pt-1">{t("stats.earlyProjects")}</p>
                  )}
                </div>
              </div>
              <div className="h-16 rounded-lg bg-muted/30 flex items-center justify-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary/70" />
                <span className="text-xs text-muted-foreground">{t("stats.movementBadge")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ QUEBRA DE REALIDADE ═══════ */}
      <section className="py-20 md:py-28 border-t border-border">
        <Section className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-10">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
              {t("pain.title")}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 text-left">
              {(t("pain.items", { returnObjects: true }) as string[]).map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-xl border border-border bg-card p-5">
                  <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
            <p className="text-lg text-muted-foreground italic">{t("pain.closing")}</p>
          </div>
        </Section>
      </section>

      {/* ═══════ A VIRADA ═══════ */}
      <section className="py-20 md:py-28 border-t border-border">
        <Section className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">{t("solution.title")}</h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
              {t("solution.text")} {t("solution.subtext")}
            </p>
          </div>
        </Section>
      </section>

      {/* ═══════ COMO FUNCIONA ═══════ */}
      <section className="py-20 md:py-28 border-t border-border">
        <Section className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-16 tracking-tight text-foreground">{t("howItWorks.title")}</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {(t("howItWorks.steps", { returnObjects: true }) as { title: string; desc: string }[]).map((s, i) => (
              <div key={i} className="text-center space-y-4">
                <span className="text-5xl font-black gradient-text">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="text-lg font-bold text-foreground">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* ═══════ 4 PILARES ═══════ */}
      <section className="py-20 md:py-28 border-t border-border">
        <Section className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-16 tracking-tight text-foreground">{t("pillars.title")}</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {pillars.map((p) => (
              <div key={p.titleKey} className="group relative rounded-2xl border border-border bg-card p-8 space-y-4 hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1">
                <div className={`${p.color} opacity-80`}>{p.icon}</div>
                <h3 className="text-xl font-bold text-foreground">{t(p.titleKey)}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{t(p.descKey)}</p>
              </div>
            ))}
          </div>
        </Section>
      </section>

      {/* ═══════ DIFERENCIAÇÃO ═══════ */}
      <section className="py-20 md:py-28 border-t border-border">
        <Section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-12 tracking-tight text-foreground">{t("comparison.title")}</h2>
            <div className="rounded-2xl border border-border overflow-hidden">
              <div className="grid grid-cols-3 text-sm font-semibold">
                <div className="p-4 bg-muted/50" />
                <div className="p-4 bg-muted/50 text-center text-muted-foreground">{(t("comparison.headers", { returnObjects: true }) as string[])[1]}</div>
                <div className="p-4 gradient-primary text-primary-foreground text-center">{(t("comparison.headers", { returnObjects: true }) as string[])[2]}</div>
              </div>
              {(t("comparison.rows", { returnObjects: true }) as string[][]).map((row) => (
                <div key={row[0]} className="grid grid-cols-3 text-sm border-t border-border">
                  <div className="p-4 font-medium text-foreground">{row[0]}</div>
                  <div className="p-4 text-center text-muted-foreground">{row[1]}</div>
                  <div className="p-4 text-center font-medium text-primary">{row[2]}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </section>

      {/* ═══════ GAMIFICAÇÃO ═══════ */}
      <section className="py-20 md:py-28 border-t border-border">
        <Section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">{t("gamification.title")}</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {gamificationItems.map((g, i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-6 space-y-3 hover:shadow-card-hover transition-all duration-300">
                  <div className="text-primary mx-auto">{g.icon}</div>
                  <h3 className="font-bold text-foreground">{t(g.titleKey)}</h3>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </section>

      {/* ═══════ DADOS REAIS — métricas contextuais ═══════ */}
      <section className="py-20 md:py-28 border-t border-border">
        <Section className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-10">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
              {stats && (stats.projects >= 5 || stats.testers >= 5 || stats.interests >= 5)
                ? t("stats.projects")
                : t("stats.earlyAdopter")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { value: stats?.projects ?? 0, label: t("stats.projects"), early: t("stats.earlyProjects") },
                { value: stats?.testers ?? 0, label: t("stats.testers"), early: t("stats.earlyTesters") },
                { value: stats?.interests ?? 0, label: t("stats.interests"), early: t("stats.earlyInterests") },
              ].map((s) => (
                <div key={s.label} className="rounded-2xl border border-border bg-card p-6 min-h-[120px] flex flex-col justify-center">
                  {s.value >= 5 ? (
                    <>
                      <p className="text-3xl md:text-4xl font-black gradient-text">{s.value}</p>
                      <p className="text-xs text-muted-foreground mt-2">{s.label}</p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground leading-snug">{s.early}</p>
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground italic">{t("stats.earlyAdopterSub")}</p>
          </div>
        </Section>
      </section>

      {/* ═══════ CTA INTERMEDIÁRIO ═══════ */}
      <section className="py-16 border-t border-border">
        <Section className="container mx-auto px-4 text-center">
          <Button size="lg" className="gradient-cta text-primary-foreground border-0 h-14 px-10 text-base font-semibold shadow-lg hover:opacity-90 transition-opacity" asChild>
            <Link to="/signup">{t("hero.ctaSecondary")} <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </Section>
      </section>

      {/* ═══════ FEATURED TOP 24H ═══════ */}
      {featured.length > 0 && (
        <section className="py-20 border-t border-border">
          <Section className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 flex items-center justify-center gap-2">
              <Flame className="h-7 w-7 text-destructive" />
              <span className="gradient-text">{t("featured.title")}</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {featured.map((p) => (
                <div key={p.id} className="ring-2 ring-primary rounded-2xl shadow-card-hover">
                  <ProductCard product={p} upvoteCount={p.upvotes} interestCount={p.interests} />
                </div>
              ))}
            </div>
          </Section>
        </section>
      )}

      {/* ═══════ CHAMADA DE MOVIMENTO ═══════ */}
      <section className="py-20 md:py-28 border-t border-border">
        <Section className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">{t("movement.title")}</h2>
            <p className="text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto">{t("movement.text")}</p>
          </div>
        </Section>
      </section>

      {/* ═══════ EARLY ADOPTERS ═══════ */}
      <section className="py-20 md:py-28 border-t border-border">
        <Section className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-accent px-4 py-1.5 text-sm text-accent-foreground font-medium">
              <Trophy className="h-4 w-4" /> Early Adopter
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">{t("earlyAdopters.title")}</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {(t("earlyAdopters.items", { returnObjects: true }) as string[]).map((item, i) => (
                <div key={i} className="rounded-xl border border-border bg-card p-5 space-y-2">
                  <div className="text-primary">{[<Award className="h-6 w-6" key="a" />, <Eye className="h-6 w-6" key="e" />, <TrendingUp className="h-6 w-6" key="t" />][i]}</div>
                  <h3 className="font-bold text-sm text-foreground">{item}</h3>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </section>

      {/* ═══════ CTA FINAL ═══════ */}
      <section className="py-24 md:py-32 border-t border-border relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-[-30%] left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full gradient-hero opacity-[0.05] blur-3xl" />
        </div>
        <Section className="container mx-auto px-4 relative">
          <div className="max-w-2xl mx-auto text-center space-y-8">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
              {t("finalCta.title")}<br />{t("finalCta.subtitle")}
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {t("closingPhrase.line1")}{" "}
              <span className="font-semibold text-foreground">{t("closingPhrase.line2")}</span>
            </p>
            <Button size="lg" className="gradient-cta text-primary-foreground border-0 h-14 px-10 text-lg font-bold shadow-xl hover:opacity-90 transition-opacity" asChild>
              <Link to="/signup">{t("finalCta.button")} <Rocket className="ml-2 h-5 w-5" /></Link>
            </Button>
          </div>
        </Section>
      </section>

      </main>

      {/* ═══════ FOOTER ═══════ */}
      <footer className="border-t border-border py-10">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2 font-bold">
            <Rocket className="h-5 w-5 text-primary" />
            <span className="gradient-text">Vitrine SaaS</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <Link to="/terms" className="hover:text-foreground transition-colors">{t("footer.terms")}</Link>
            <Link to="/privacy" className="hover:text-foreground transition-colors">{t("footer.privacy")}</Link>
            <Link to="/contact" className="hover:text-foreground transition-colors">{t("footer.contact")}</Link>
          </div>
          <p className="text-xs text-muted-foreground">© 2026 Vitrine SaaS.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
